import React, { forwardRef, type SelectHTMLAttributes } from "react";
import classNames from "classnames";

interface NativeSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string | JSX.Element;
  error?: string | JSX.Element;
  containerClassName?: string;
  variant?: "default" | "glass";
}

export const NativeSelect = forwardRef<HTMLSelectElement, NativeSelectProps>(
  ({ label, error, containerClassName, className, variant = "default", children, ...props }, ref) => {
    return (
      <div className={classNames("min-w-0 w-full font-sans", containerClassName)}>
        {label && (
          <div className="mb-1.5 text-sm font-medium text-primary-700 dark:text-primary-300">
            {label}
          </div>
        )}
        <div className="relative flex min-w-0">
          <select
            ref={ref}
            className={classNames(
              "w-full font-sans text-sm font-semibold outline-none transition-all cursor-pointer appearance-none pr-10 pl-4",
              // Default variant styling
              variant === "default" && [
                "h-10 rounded-lg px-4 py-2 border border-solid",
                "bg-white dark:bg-transparent bg-gradient-to-b from-white to-primary-50/50 dark:from-content-900 dark:to-primary-950/70",
                "text-primary-700 dark:text-content-100",
                "border-primary-200/80 dark:border-primary-800/70",
                "shadow-[inset_0_1px_2px_rgba(0,0,0,0.06)]",
                "dark:shadow-[inset_0_1px_2px_rgba(0,0,0,0.24)]",
                "hover:border-primary-300 dark:hover:border-primary-700",
                "focus:border-primary-400 dark:focus:border-primary-400",
                "focus:ring-2 focus:ring-primary-300/25 dark:focus:ring-primary-450/25",
              ],
              // Glass variant styling (used for headers/sorting)
              variant === "glass" && [
                "h-12 rounded-2xl border",
                "border-white/20 bg-white/30 text-slate-800 backdrop-blur-xl shadow-lg shadow-black/5",
                "dark:border-content-700/45 dark:bg-content-900/45 dark:text-content-100",
                "hover:bg-white/40 dark:hover:bg-content-900/60",
                "focus:border-primary-300/40 focus:ring-2 focus:ring-primary-300/25 dark:focus:border-primary-400/50",
              ],
              error && "border-error-500 dark:border-error-500",
              className
            )}
            {...props}
          >
            {children}
          </select>
          {/* Custom Caret Arrow to style it nicely since we hid the default appearance */}
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <span className={classNames(
              "icon-[ph--caret-down] h-4 w-4 transition-transform",
              variant === "glass"
                ? "text-slate-500 dark:text-content-400"
                : "text-primary-400 dark:text-primary-500"
            )} />
          </div>
        </div>
        {error && (
          <p className="mt-1.5 text-sm font-medium text-error-600 dark:text-error-400">
            {error}
          </p>
        )}
      </div>
    );
  }
);

NativeSelect.displayName = "NativeSelect";
